import { useCallback } from 'react';
import { Control, Controller, FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import AddressAutocomplete, { type GeocodedAddressData } from '@/shared/components/AddressAutocomplete';
import type { ProviderEditorFormData } from '../schemas';

type BasicInformationSectionProps = {
  control: Control<ProviderEditorFormData>;
  errors: FieldErrors<ProviderEditorFormData>;
  setValue: UseFormSetValue<ProviderEditorFormData>;
};

type FormFieldProps = {
  label: string;
  required?: boolean;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
  hint?: string;
};

function FormField({
  label,
  required = false,
  error,
  htmlFor,
  children,
  hint,
}: FormFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

/**
 * BasicInformationSection - Core provider business information
 *
 * Contains:
 * - Company name (required)
 * - Business address
 * - Tagline
 * - Company overview
 * - Provider bio
 * - Service offerings description
 * - Accepting new clients toggle
 * - Established year
 * - Years in business
 * - Geographic coordinates (lat/lng)
 */
export function BasicInformationSection({
  control,
  errors,
  setValue,
}: BasicInformationSectionProps) {
  const currentYear = new Date().getFullYear();

  // Read current address from form for AddressAutocomplete controlled value
  const businessAddress = useWatch({ control, name: 'provider.businessAddress' }) || '';

  /** Called when user types in the address field (no place selected yet) */
  const handleAddressTextChange = useCallback(
    (address: string) => {
      setValue('provider.businessAddress', address, { shouldDirty: true });
      // Clear coordinates while typing (not yet geocoded)
      setValue('provider.latitude', null, { shouldDirty: true });
      setValue('provider.longitude', null, { shouldDirty: true });
      // Same for Checkr location codes: they belong to the previously selected
      // place, not to free-typed text. Picking a suggestion sets them again.
      setValue('provider.businessStateUspsCode', null, { shouldDirty: true });
      setValue('provider.businessCountryIsoCode', null, { shouldDirty: true });
    },
    [setValue],
  );

  /** Called when user selects a Google Places suggestion (includes lat/lng) */
  const handleAddressGeocode = useCallback(
    (data: GeocodedAddressData) => {
      setValue('provider.businessAddress', data.description, { shouldDirty: true });
      setValue('provider.latitude', data.latitude, { shouldDirty: true });
      setValue('provider.longitude', data.longitude, { shouldDirty: true });
      // Feature 099 handoff (handoff-work-location-fields.md): captured at this
      // same selection event so Checkr invitations don't need a separate geocode.
      setValue('provider.businessStateUspsCode', data.stateUspsCode, { shouldDirty: true });
      setValue('provider.businessCountryIsoCode', data.countryIsoCode, { shouldDirty: true });
    },
    [setValue],
  );

  return (
    <div className="space-y-6">
      {/* Company Name */}
      <FormField
        label="Company Name"
        required
        htmlFor="company-name"
        error={errors.provider?.companyName?.message}
        hint="Legal business name or DBA"
      >
        <Controller
          name="provider.companyName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="company-name"
              className={errors.provider?.companyName ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Business Address (Google Places Autocomplete) */}
      <FormField
        label="Business Address"
        error={errors.provider?.businessAddress?.message}
        hint="Start typing to search Google Places. Latitude and longitude are resolved automatically."
      >
        <div className="[&_input]:flex [&_input]:h-10 [&_input]:w-full [&_input]:rounded-md [&_input]:border [&_input]:border-input [&_input]:bg-background [&_input]:px-3 [&_input]:py-2 [&_input]:text-sm [&_input]:ring-offset-background [&_input]:focus-visible:outline-none [&_input]:focus-visible:ring-2 [&_input]:focus-visible:ring-ring [&_input]:focus-visible:ring-offset-2">
          <AddressAutocomplete
            selectedAddress={businessAddress}
            setSelectedAddress={handleAddressTextChange}
            onAddressSelectAndGeocode={handleAddressGeocode}
            resetTrigger={0}
          />
        </div>
      </FormField>

      {/* Address Visibility Control (Feature 057) */}
      <FormField
        label="Address Visibility"
        htmlFor="provider-hideBusinessAddress"
        hint="Control whether the business address is shown on the public profile. This only affects display - the address is still used for search and distance calculations."
      >
        <Controller
          name="provider.hideBusinessAddress"
          control={control}
          render={({ field }) => (
            <div className="flex items-center gap-3 cursor-pointer">
              <input
                id="provider-hideBusinessAddress"
                type="checkbox"
                checked={field.value ?? false}
                onChange={(e) => field.onChange(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                aria-describedby="address-visibility-description"
              />
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-700">
                  Hide address from public profile
                </span>
                <span
                  id="address-visibility-description"
                  className="text-xs text-gray-500"
                >
                  When enabled, the business address will not be displayed to care seekers or financial advisors
                </span>
              </div>
            </div>
          )}
        />
      </FormField>

      {/* Latitude & Longitude (read-only, resolved from Google Places) */}
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Latitude"
          htmlFor="latitude"
          hint="Resolved from selected address"
        >
          <Controller
            name="provider.latitude"
            control={control}
            render={({ field }) => (
              <Input
                value={field.value ?? ''}
                id="latitude"
                disabled
                className="bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            )}
          />
        </FormField>

        <FormField
          label="Longitude"
          htmlFor="longitude"
          hint="Resolved from selected address"
        >
          <Controller
            name="provider.longitude"
            control={control}
            render={({ field }) => (
              <Input
                value={field.value ?? ''}
                id="longitude"
                disabled
                className="bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            )}
          />
        </FormField>
      </div>

      {/* Tagline */}
      <FormField
        label="Tagline"
        htmlFor="tagline"
        error={errors.provider?.tagline?.message}
        hint="Brief description (max 255 characters)"
      >
        <Controller
          name="provider.tagline"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value || ''}
              id="tagline"
              maxLength={255}
              className={errors.provider?.tagline ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Company Overview */}
      <FormField
        label="Company Overview"
        htmlFor="company-overview"
        error={errors.provider?.companyOverview?.message}
        hint="High-level overview of the company, mission, and services"
      >
        <Controller
          name="provider.companyOverview"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              value={field.value || ''}
              id="company-overview"
              rows={6}
              className={errors.provider?.companyOverview ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Provider Bio */}
      <FormField
        label="Provider Bio"
        htmlFor="provider-bio"
        error={errors.provider?.providerBio?.message}
        hint="Background, experience, and personal approach"
      >
        <Controller
          name="provider.providerBio"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              value={field.value || ''}
              id="provider-bio"
              rows={6}
              className={errors.provider?.providerBio ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Service Offerings */}
      <FormField
        label="Service Offerings Summary"
        htmlFor="service-offerings"
        error={errors.provider?.serviceOfferings?.message}
        hint="Brief overview of services (10-1000 characters)"
      >
        <Controller
          name="provider.serviceOfferings"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              value={field.value || ''}
              id="service-offerings"
              rows={4}
              className={errors.provider?.serviceOfferings ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Client Acceptance */}
      <FormField
        label="Accepting New Clients"
        htmlFor="new-client-acceptance"
        error={errors.provider?.newClientAcceptance?.message}
        hint="Whether provider is accepting new clients"
      >
        <Controller
          name="provider.newClientAcceptance"
          control={control}
          render={({ field }) => (
            <select
              id="new-client-acceptance"
              value={field.value === true ? 'true' : 'false'}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => field.onChange(e.target.value === 'true')}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="true">Yes, Accepting</option>
              <option value="false">No, Not Accepting</option>
            </select>
          )}
        />
      </FormField>

      {/* bQuest Preferred Provider (Feature 083) */}
      <FormField
        label="bQuest Preferred Provider"
        htmlFor="bquest-preferred"
        error={errors.provider?.isBquestPreferred?.message}
        hint="Show this provider in the curated 'bQuest Preferred Providers' list on every family's My Team"
      >
        <Controller
          name="provider.isBquestPreferred"
          control={control}
          render={({ field }) => (
            <select
              id="bquest-preferred"
              value={field.value === true ? 'true' : 'false'}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => field.onChange(e.target.value === 'true')}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="true">Yes, Preferred</option>
              <option value="false">No</option>
            </select>
          )}
        />
      </FormField>

      {/* Established Year & Years in Business */}
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Established Year"
          htmlFor="established-year"
          error={errors.provider?.establishedYear?.message}
          hint={`Year founded (1800-${currentYear})`}
        >
          <Controller
            name="provider.establishedYear"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  const value = e.target.value === '' || isNaN(parsed) ? null : parsed;
                  field.onChange(value);
                }}
                id="established-year"
                type="number"
                min={1800}
                max={currentYear}
                className={errors.provider?.establishedYear ? 'border-red-500' : ''}
              />
            )}
          />
        </FormField>

        <FormField
          label="Years in Business"
          htmlFor="years-in-business"
          error={errors.provider?.yearsInBusiness?.message}
          hint="Total years operating"
        >
          <Controller
            name="provider.yearsInBusiness"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  const value = e.target.value === '' || isNaN(parsed) ? null : parsed;
                  field.onChange(value);
                }}
                id="years-in-business"
                type="number"
                min={0}
                className={errors.provider?.yearsInBusiness ? 'border-red-500' : ''}
              />
            )}
          />
        </FormField>
      </div>
    </div>
  );
}

